// CORE domain: organization, users, workspaces, projects, brands (§5–13, backend §5–11).
import { forgetRuns, runTotals } from './runs';
import { assetCount, forgetAssets } from './library';
import { db, byId, eq, sortBy, uniq, insert, update, removeLocal, ORG_ID, CURRENT_USER_ID, whenPersisted, loadTable, refreshTable } from './core';

export const getOrganization = () => byId('organizations', ORG_ID);
export const currentUser = () => byId('users', CURRENT_USER_ID);
export const getUser = (id?: any) => byId('users', id) || (id === 'system' ? { id, name: 'Platform', avatar_hue: 210 } : { id, name: id === 'usr_platform_admin' ? 'Platform Admin' : 'Unknown', avatar_hue: 0 });
export const orgMembers = () => eq('organization_members', 'organization_id', ORG_ID).map((m?: any) => ({ ...m, user: getUser(m.user_id) }));

// Workspaces
// Workspaces are the shared project context. organization_id remains provenance
// for files, billing and audit, but never filters the shared project directory.
export const listWorkspaces = () => db.workspaces;
export const getWorkspace = (id?: any) => byId('workspaces', id);
export const workspaceMembers = (wsId?: any) => eq('workspace_members', 'workspace_id', wsId).map((m?: any) => ({ ...m, user: getUser(m.user_id) }));
export const workspaceBrands = (wsId?: any) => eq('workspace_brands', 'workspace_id', wsId).map((r?: any) => byId('brands', r.brand_id)).filter(Boolean);

// Projects
export function projectBrands(projectId?: any) {
  const project = byId('projects', projectId);
  const brandId = project?.brand_id || project?.data?.brand_id;
  return brandId ? [byId('brands', brandId)].filter(Boolean) : [];
}
export const getProject = (id?: any) => byId('projects', id);

const projectNameOrder = new Intl.Collator('en', { sensitivity: 'base', numeric: true, ignorePunctuation: true });

export function listProjects({ workspaceId, brandId, status, creator, view = 'all', q, favorites = [], recents = [] }: any = {}) {
  let rows = db.projects;
  if (view !== 'archived') rows = rows.filter((p?: any) => p.status !== 'archived');
  if (view === 'archived') rows = rows.filter((p?: any) => p.status === 'archived');
  if (view === 'active') rows = rows.filter((p?: any) => p.status === 'active' || p.status === 'in_production');
  if (view === 'mine') rows = rows.filter((p?: any) => p.created_by === CURRENT_USER_ID);
  if (view === 'favorites') rows = rows.filter((p?: any) => favorites.some((f?: any) => f.entity_type === 'project' && f.entity_id === p.id));
  if (workspaceId) rows = rows.filter((p?: any) => p.workspace_id === workspaceId);
  if (brandId) rows = rows.filter((p?: any) => projectBrands(p.id).some((b?: any) => b.id === brandId));
  if (status) rows = rows.filter((p?: any) => p.status === status);
  if (creator) rows = rows.filter((p?: any) => p.created_by === creator);
  if (q) { const s = q.toLowerCase(); rows = rows.filter((p?: any) => (p.name + ' ' + p.description).toLowerCase().includes(s)); }
  if (view === 'recent') {
    const order = recents.filter((r?: any) => r.entity_type === 'project').map((r?: any) => r.entity_id);
    return rows.filter((p?: any) => order.includes(p.id)).sort((a?: any, b?: any) => order.indexOf(a.id) - order.indexOf(b.id));
  }
  return [...rows].sort((a, b) => projectNameOrder.compare(a.name || '', b.name || ''));
}

export function projectStats(projectId?: any) {
  const flows = eq('flows', 'project_id', projectId);
  const flowIds = new Set(flows.map((f?: any) => f.id));
  const totals = runTotals({ projectId });
  const lastRun = sortBy(db.flow_last_runs.filter((r?: any) => flowIds.has(r.flow_id)), 'created_at')[0] || null;
  return {
    flows: flows.length,
    runs: totals.runs,
    failed: totals.failed,
    running: totals.running,
    cost: totals.cost,
    assets: assetCount({ projectId }) ?? '…',
    lastRun,
  };
}

// Brands (global, §12)
export const listBrands = () => eq('brands', 'organization_id', ORG_ID);
export const getBrand = (id?: any) => byId('brands', id);
export async function createBrand({ name, description = '', signupEnabled = false }: { name: string; description?: string; signupEnabled?: boolean }) {
  name = name.trim();
  if (!name) throw new Error('Enter a brand name.');
  const response = await fetch('/api/v1/admin/brands', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description: description.trim(), signupEnabled }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not create brand (${response.status})`);
  await refreshTable('brands');
  return body.id as string;
}
export async function updateBrand(id: string, {name, description}: {name: string; description: string}) {
  name = name.trim();
  if (!name) throw new Error('Enter a brand name.');
  const brand = getBrand(id);
  if (!brand) throw new Error('Brand not found.');
  const response = await fetch('/api/v1/ui/brands', {
    method: 'PATCH',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({id, name, description: description.trim(), data: brand.data || {}}),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not update brand (${response.status})`);
  await refreshTable('brands');
  return getBrand(id);
}
export async function deleteBrand(id: string) {
  const response = await fetch(`/api/v1/brands/${encodeURIComponent(id)}`, { method: 'DELETE' });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not delete brand (${response.status})`);
  await refreshTable('brands');
  return id;
}
export const brandProjects = (brandId?: any) => db.projects.filter((p?: any) => (p.brand_id || p.data?.brand_id) === brandId && p.status !== 'archived');
export const brandWorkspaces = (brandId?: any) => eq('workspace_brands', 'brand_id', brandId).map((r?: any) => byId('workspaces', r.workspace_id));
export function brandStats(brandId?: any) {
  const projects = brandProjects(brandId).map((p?: any) => p.id);
  return {
    projects: projects.length,
    workspaces: brandWorkspaces(brandId).length,
    assets: assetCount({ brandOwnerId: brandId }) ?? '…',
    flows: db.flows.filter((f?: any) => projects.includes(f.project_id)).length,
    runs: runTotals({ projectIds: projects }).runs,
  };
}

// Settings resources
export const connectionsFor = (scope?: any, scopeId?: any) => db.connections.filter((c?: any) => (!scope || c.scope === scope) && (!scopeId || c.scope_id === scopeId));
export const variablesFor = (scope?: any, scopeId?: any) => db.variables.filter((v?: any) => (!scope || v.scope === scope) && (!scopeId || v.scope_id === scopeId));

// Variable inheritance: org → workspace → project → flow, lower scope wins (backend §26)
export function resolveVariables({ workspaceId, projectId, flowId }: any) {
  const project = projectId ? getProject(projectId) : null;
  const workspace = workspaceId ? getWorkspace(workspaceId) : null;
  const organizationId = project?.organization_id || workspace?.organization_id || ORG_ID;
  const chain = [['organization', organizationId], ['workspace', workspaceId], ['project', projectId], ['flow', flowId]];
  const out: any = {};
  chain.forEach(([scope, id]: any) => {
    if (!id) return;
    variablesFor(scope, id).forEach((v?: any) => { out[v.key] = { ...v, overrides: out[v.key] ? [...(out[v.key].overrides || []), out[v.key]] : [] }; });
  });
  return Object.values(out);
}

export const scopeName = (scope?: any, id?: any) => {
  if (scope === 'organization') return getOrganization().name;
  if (scope === 'workspace') return getWorkspace(id)?.name;
  if (scope === 'project') return getProject(id)?.name;
  if (scope === 'flow') return byId('flows', id)?.name;
  return id;
};

// ── mutations (in-memory; backend: POST/PATCH /api/projects) ──
export async function createProject({ name, workspaceId, brandId, description = '', userId }: any) {
  const now = new Date().toISOString();
  name = String(name || '').trim();
  if (!name) throw new Error('Enter a project name.');
  await Promise.all([loadTable('workspaces'), loadTable('projects'), ...(brandId ? [loadTable('brands')] : [])]);
  const workspace = workspaceId ? getWorkspace(workspaceId) : null;
  if (!workspace) throw new Error('Select an available workspace for the project.');
  const id = crypto.randomUUID();
  const brand = brandId ? byId('brands', brandId) : null;
  if (brandId && !brand) throw new Error('Select an available brand for the project.');
  const project = insert('projects', { id, organization_id: workspace.organization_id, workspace_id: workspaceId, name, description, status: 'active', brand_id: brandId || null, tags: [], type: null, channel: null, cover: { hue: brand?.cover?.hue ?? [...name].reduce((h?: any, c?: any) => (h * 31 + c.charCodeAt(0)) % 360, 7), glyph: brand?.cover?.glyph || 'image' }, created_by: userId, created_at: now, updated_at: now, archived_at: null });
  const saved = await whenPersisted(project);
  return saved.id;
}

export async function updateProject(id: string, { name, description, status, brandId }: { name: string; description: string; status: string; brandId: string | null }) {
  name = name.trim();
  if (!name) throw new Error('Enter a project name.');
  if (!getProject(id)) throw new Error('Project not found.');
  if (brandId && !byId('brands', brandId)) throw new Error('Select an available brand for the project.');
  const response = await fetch('/api/v1/ui/projects', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, name, description: description.trim(), status, brand_id: brandId, updated_at: new Date().toISOString() }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not update project (${response.status})`);
  await refreshTable('projects');
  return getProject(id);
}
export const archiveProject = (id?: any) => update('projects', id, { status: 'archived', archived_at: new Date().toISOString() });
export const restoreProject = (id?: any) => update('projects', id, { status: 'active', archived_at: null });

export async function deleteProject(id: string) {
  await Promise.all([loadTable('projects'), loadTable('flows')]);
  const response = await fetch(`/api/v1/projects/${encodeURIComponent(id)}`, { method: 'DELETE' });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not delete project (${response.status})`);
  if (body.id !== id || body.deleted !== true) throw new Error(body.error || 'The server did not confirm project deletion.');

  const flowIds = db.flows.filter(flow => flow.project_id === id).map(flow => flow.id);
  flowIds.forEach(flowId => removeLocal('flows', flowId));
  db.asset_usages = db.asset_usages.filter(usage => usage.project_id !== id && !flowIds.includes(usage.flow_id));
  db.favorites = db.favorites.filter(favorite => !['project', 'flow'].includes(favorite.entity_type) || (favorite.entity_id !== id && !flowIds.includes(favorite.entity_id)));
  db.recents = db.recents.filter(recent => !['project', 'flow'].includes(recent.entity_type) || (recent.entity_id !== id && !flowIds.includes(recent.entity_id)));
  removeLocal('projects', id);
  db.assets.forEach(asset => {
    if (asset.project_id !== id) return;
    asset.project_id = null;
  });
  forgetRuns();
  forgetAssets();
  for (const table of ['projects', 'flows', 'runs']) window.dispatchEvent(new CustomEvent('cf:data-updated', { detail: table }));
  return id;
}

export async function createWorkspace({ name, description = '', userId, type = 'unbranded' }: any) {
  const now = new Date().toISOString();
  name = String(name || '').trim();
  if (!name) throw new Error('Enter a workspace name.');
  await loadTable('workspaces');
  const id = crypto.randomUUID();
  const workspace = insert('workspaces', { id, organization_id: ORG_ID, name, type, description, status: 'active', created_by: userId, created_at: now, updated_at: now, archived_at: null });
  return (await whenPersisted(workspace)).id;
}
export async function updateWorkspace(id: string, { name, description }: { name: string; description: string }) {
  const workspace = getWorkspace(id);
  if (!workspace) throw new Error('Workspace not found.');
  name = name.trim();
  if (!name) throw new Error('Enter a workspace name.');
  const response = await fetch('/api/v1/ui/workspaces', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, name, description: description.trim(), type: workspace.type }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not update workspace (${response.status})`);
  await refreshTable('workspaces');
  return getWorkspace(id);
}
export async function deleteWorkspace(id: string) {
  const response = await fetch(`/api/v1/workspaces/${encodeURIComponent(id)}`, { method: 'DELETE' });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not delete workspace (${response.status})`);

  const projectIds = db.projects.filter(project => project.workspace_id === id).map(project => project.id);
  const flowIds = db.flows.filter(flow => projectIds.includes(flow.project_id)).map(flow => flow.id);
  const removeChildren = (table: any, rows: any[], field: string, ids: string[]) => {
    for (const row of rows.filter(candidate => ids.includes(candidate[field]))) removeLocal(table, row.id);
  };
  removeChildren('workspace_brands', db.workspace_brands, 'workspace_id', [id]);
  removeChildren('workspace_members', db.workspace_members, 'workspace_id', [id]);
  removeChildren('project_brands', db.project_brands, 'project_id', projectIds);
  removeChildren('project_members', db.project_members, 'project_id', projectIds);
  removeChildren('flows', db.flows, 'project_id', projectIds);
  removeChildren('flow_versions', db.flow_versions, 'flow_id', flowIds);
  removeChildren('nodes', db.nodes, 'flow_id', flowIds);
  removeChildren('edges', db.edges, 'flow_id', flowIds);
  removeChildren('node_groups', db.node_groups, 'flow_id', flowIds);
  forgetRuns();
  removeChildren('projects', db.projects, 'workspace_id', [id]);
  removeChildren('favorites', db.favorites, 'entity_id', projectIds);
  removeChildren('recents', db.recents, 'entity_id', projectIds);
  db.assets.forEach(asset => {
    if (asset.workspace_id === id) asset.workspace_id = null;
    if (projectIds.includes(asset.project_id)) asset.project_id = null;
  });
  removeLocal('workspaces', id);
  forgetAssets();
  for (const table of ['assets', 'projects', 'flows', 'runs']) window.dispatchEvent(new CustomEvent('cf:data-updated', { detail: table }));
  return id;
}
export function createConnection({ name, provider, scope, scopeId, userId }: any) {
  const now = new Date().toISOString();
  const id = 'con_new_' + Date.now();
  return insert('connections', { id, organization_id: ORG_ID, name, provider, scope, scope_id: scopeId, secret_reference: `vault://cf/${id}`, status: 'active', created_by: userId, created_at: now, updated_at: now, last_used_at: null });
}
export const setMemberRole = (memberId?: any, role?: any) => {
  const member = byId('organization_members', memberId);
  if (!member) return null;
  member.role = role;
  void fetch(`/api/v1/organizations/${member.organization_id}/members/${member.user_id}`, { method:'PATCH', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ role }) }).then(async response => {
    if (!response.ok) { const body = await response.json().catch(() => ({})); window.dispatchEvent(new CustomEvent('cf:persistence-error', { detail:body.error || 'Member role was not saved' })); }
  }).catch(error => window.dispatchEvent(new CustomEvent('cf:persistence-error', { detail:error.message })));
  return member;
};

export const creators = () => uniq(db.projects.map((p?: any) => p.created_by)).map((id?: any) => byId('users', id)).filter(Boolean);
